import { CheckCircle2, Clock, AlertTriangle, ExternalLink } from "lucide-react";

const ESTADO_CONFIG = {
  completado: { icon: CheckCircle2, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-500" },
  agendado: { icon: Clock, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-500" },
  pendiente: { icon: Clock, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-500" },
  urgente: { icon: AlertTriangle, bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", badge: "bg-rose-500" },
};

const ETAPA_COLORS = {
  "Acceso": "#7C3AED",
  "Atención": "#3B82F6",
  "Diagnóstica": "#14B8A6",
  "Tratamiento": "#F59E0B",
  "Financiero": "#EC4899",
  "Seguimiento": "#6366F1",
};

export default function ProximosPasos({ pasos, progreso }) {
  if (!pasos || pasos.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5" data-testid="proximos-pasos">
      {/* Header + Progress */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">🗺️ Próximos Pasos en tu Viaje</h3>
            <p className="text-sm text-slate-500 mt-0.5">Basado en tus indicaciones médicas y actividad reciente</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-slate-900 tabular-nums">{progreso.completados}/{progreso.total}</span>
            <p className="text-xs text-slate-500">pasos completados</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${progreso.pct}%` }}
          />
        </div>
        <div className="mt-1 text-xs text-slate-500 tabular-nums">{progreso.pct}% completado</div>
      </div>

      {/* Steps list */}
      <div className="space-y-3">
        {pasos.map((paso) => {
          const cfg = ESTADO_CONFIG[paso.estado] || ESTADO_CONFIG.pendiente;
          const Icon = cfg.icon;
          const etapaColor = ETAPA_COLORS[paso.etapa] || "#64748B";

          return (
            <div
              key={paso.id}
              className={`rounded-xl border p-4 transition-all ${cfg.bg} ${cfg.border} ${paso.estado === "urgente" ? "ring-1 ring-rose-300" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-white shrink-0 ${cfg.badge}`}>
                  <Icon className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${cfg.text}`}>{paso.titulo}</span>
                    {paso.estado === "urgente" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-rose-500 animate-pulse">URGENTE</span>
                    )}
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white" style={{ backgroundColor: etapaColor }}>
                      {paso.etapa}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{paso.descripcion}</p>
                  {paso.fecha && (
                    <span className="text-[10px] text-slate-400 tabular-nums mt-1 inline-block">
                      {paso.fecha} · hace {paso.diasDesde} días
                    </span>
                  )}
                  {/* Action button for pending/urgent */}
                  {(paso.estado === "pendiente" || paso.estado === "urgente") && paso.accion && (
                    <div className="mt-2">
                      <a
                        href={paso.link || "https://agenda.ucchristus.cl"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition hover:-translate-y-0.5 ${
                          paso.estado === "urgente"
                            ? "bg-white border-rose-200 text-rose-700 hover:bg-rose-50"
                            : "bg-white border-amber-200 text-amber-700 hover:bg-amber-50"
                        }`}
                      >
                        {paso.accion}
                        <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
